import * as CookieConsent from 'vanilla-cookieconsent';

import { TranslateService } from '@ngx-translate/core';
import { inject, Injectable } from '@angular/core';
import { CookieConsentService } from './cookie-consent.service';
import { ICookieConsent } from '../models/cookie-consent.model';
import { SentryService } from './sentry.service';

@Injectable({
  providedIn: 'root',
})
export class CookieRunService {
  readonly #cookieConsentService = inject(CookieConsentService);
  readonly #translateService = inject(TranslateService);
  readonly #sentryService = inject(SentryService);

  public run(cookiesPolicyLink: string): void {
    CookieConsent.run({
      manageScriptTags: false,
      disablePageInteraction: true,

      guiOptions: {
        consentModal: {
          layout: 'box',
          position: 'middle center',
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: 'box',
          equalWeightButtons: true,
          flipButtons: false,
        },
      },

      onFirstConsent: ({ cookie }) => {
        if (cookie.categories.includes('analytics')) {
          this.#sentryService.init();
        }

        this.#cookieConsentService
          .create(
            new ICookieConsent({
              statistics: cookie.categories.includes('analytics'),
              uuid: cookie.consentId,
            })
          )
          .subscribe();
      },

      onConsent: ({ cookie }) => {
        if (cookie.categories.includes('analytics')) {
          this.#sentryService.init();
        }
      },

      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {
          enabled: false,
          readOnly: false,
          autoClear: {
            cookies: [{ name: /^(_ga|_gid)/ }],
          },
        },
      },

      language: {
        default: 'fr',
        translations: {
          fr: {
            consentModal: {
              title: this.#translateService.instant('cookie-consent.consentModal.title'),
              description:
                this.#translateService.instant('cookie-consent.consentModal.description.content') +
                '<a href="' +
                cookiesPolicyLink +
                '" target="_blank">' +
                this.#translateService.instant('cookie-consent.consentModal.description.linkName') +
                '</a>.',
              acceptAllBtn: this.#translateService.instant('cookie-consent.consentModal.acceptAllBtn'),
              acceptNecessaryBtn: this.#translateService.instant('cookie-consent.consentModal.acceptNecessaryBtn'),
              showPreferencesBtn: this.#translateService.instant('cookie-consent.consentModal.showPreferencesBtn'),
            },
            preferencesModal: {
              title: this.#translateService.instant('cookie-consent.preferencesModal.title'),
              acceptAllBtn: this.#translateService.instant('cookie-consent.preferencesModal.acceptAllBtn'),
              acceptNecessaryBtn: this.#translateService.instant('cookie-consent.preferencesModal.acceptNecessaryBtn'),
              savePreferencesBtn: this.#translateService.instant('cookie-consent.preferencesModal.savePreferencesBtn'),
              closeIconLabel: this.#translateService.instant('cookie-consent.preferencesModal.closeIconLabel'),
              serviceCounterLabel: this.#translateService.instant('cookie-consent.preferencesModal.serviceCounterLabel'),
              sections: [
                {
                  title: this.#translateService.instant('cookie-consent.sections.description.title'),
                  description: this.#translateService.instant('cookie-consent.sections.description.content'),
                },
                {
                  title: this.#translateService.instant('cookie-consent.sections.necessary.title'),
                  description: this.#translateService.instant('cookie-consent.sections.necessary.content'),
                  linkedCategory: this.#translateService.instant('cookie-consent.sections.necessary.linkedCategory'),
                },
                {
                  title: this.#translateService.instant('cookie-consent.sections.analytics.title'),
                  description: this.#translateService.instant('cookie-consent.sections.analytics.content'),
                  linkedCategory: this.#translateService.instant('cookie-consent.sections.analytics.linkedCategory'),
                },
              ],
            },
          },
        },
      },
    }).then();
  }
}
